/* Shared per-lot profit math — single source of truth for "how much
   did GFI actually make on this lot", used by ProfitTracker.js (the
   full module) and BillRegistry.js's "My Profit" panel (a compact
   summary for one bill). Extracted here for the same reason as
   seasonComparisonCalc.js: this exact math had drifted into 3
   independent copies this session (ProfitTracker.js, SeasonRegistry.js,
   BillRegistry.js) and each one had to be found and fixed separately
   for the same split-lot-across-invoices double-counting bug. A 4th
   copy (SeasonRegistry.js's calcSeasonProfits) still exists and was
   NOT folded into this file — out of scope for this pass, flagged
   separately.

   Kept in its own file (not duplicated per-registry) because it's pure
   calculation with no UI, unlike the registry Field/Section/Badge
   components CLAUDE.md says to keep duplicated. */

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }

/* Real per-invoice case rows for one lot — fixes the split-lot profit
   duplication bug: when a lot is divided across 2+ invoices (PUs) via
   "Add from shipment" in InvoiceRegistry.js, EACH invoice's lines[]
   inherits a full COPY of the shipment's product lines (i.e. the
   lot's TOTAL cases), even though invoice.palletAllocations is what
   actually gets edited to reflect the real split (e.g. 480/240 of
   720). Summing lines[].cases per invoice therefore double(or N-)
   counts the lot's cases/revenue once per invoice sharing it.
   palletAllocations ({lot, palletNum, cases}) is the trusted source —
   same one PalletBreakdown.js and the packing-list builder use — so
   resolve each allocation's pallet against the shipment manifest to
   get its product, then match that product (productBase+category+
   size) to the invoice line carrying salePrice/commissionRate/
   saleStatus. Pre-FASE-3 invoices with no palletAllocations for this
   lot fall back to the legacy lines[]-only behavior unchanged. */
export function invoiceRowsForLot(inv, lot, shipByLot) {
  var allocs = (inv.palletAllocations || []).filter(function (a) { return a && String(a.lot) === String(lot) && (parseInt(a.cases, 10) || 0) > 0; });
  if (allocs.length > 0) {
    var shp = shipByLot[lot];
    var manifest = shp ? (shp.palletManifest || []) : [];
    var lines = inv.lines || [];
    var out = [];
    allocs.forEach(function (a) {
      var meta = manifest.find(function (p) { return String(p.palletNum) === String(a.palletNum); }) || null;
      var line = meta && lines.find(function (l) {
        return (l.productBase || l.productName || "") === (meta.productBase || "")
          && (l.category || "") === (meta.category || "")
          && (l.size || "") === (meta.size || "");
      });
      if (!line && meta) {
        line = lines.find(function (l) {
          return (l.productBase || l.productName || "") === (meta.productBase || "")
            && (l.category || "") === (meta.category || "");
        });
      }
      var saleStatus = line ? line.saleStatus : "";
      if (saleStatus === "dumped" || saleStatus === "donated" || saleStatus === "samples" || saleStatus === "transfer") return;
      out.push({
        product: (line && (line.productName || line.productBase)) || (meta && meta.productBase) || "?",
        cases: parseInt(a.cases, 10) || 0,
        salePrice: line ? (parseFloat(line.salePrice) || 0) : 0,
        commissionRate: line ? line.commissionRate : undefined,
      });
    });
    return out;
  }
  /* Legacy fallback — invoices saved before palletAllocations existed. */
  return (inv.lines || []).filter(function (l) {
    if (l.lot && l.lot !== lot) return false;
    return l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer";
  }).map(function (l) {
    return { product: l.productName || l.productBase || "?", cases: parseInt(l.cases, 10) || 0, salePrice: parseFloat(l.salePrice) || 0, commissionRate: l.commissionRate };
  });
}

/* Index expense bills by lot — {actualCost, feeType, category} rows.
   Verbatim copy of ProfitTracker.js's original indexer (unchanged by
   the palletAllocations fix, since expense bills aren't invoice-line
   driven). Computed over ALL lots at once for the (rare) case where a
   single expense bill's fee lines span more than one lot. */
export function buildExpenseByLot(expenseBills, shipments) {
  var expenseByLot = {};
  expenseBills.forEach(function (eb) {
    var fees = eb.fees || [];
    var dist = eb.distribution || [];
    var linkedLots = eb.linkedLots || [];
    if (fees.length > 0) {
      fees.forEach(function (fe) {
        if (!fe.shipmentId) return;
        var shp = shipments.find(function (s) { return s.id === fe.shipmentId; });
        var lot = shp ? shp.lot : null;
        if (!lot) return;
        if (!expenseByLot[lot]) expenseByLot[lot] = [];
        var fname = fe.feeType === "__custom__" ? (fe.feeCustom || "Other") : (fe.feeType || eb.category || "other");
        expenseByLot[lot].push({ feeType: fname, category: eb.category || "other", vendor: eb.vendor || "", actualCost: parseFloat(fe.amount) || 0 });
      });
      var unlinked = fees.filter(function (fe) { return !fe.shipmentId && parseFloat(fe.amount) > 0; });
      if (unlinked.length > 0 && linkedLots.length > 0) {
        unlinked.forEach(function (fe) {
          var perLot = (parseFloat(fe.amount) || 0) / linkedLots.length;
          var fname = fe.feeType === "__custom__" ? (fe.feeCustom || "Other") : (fe.feeType || "other");
          linkedLots.forEach(function (sid) { var shp = shipments.find(function (s) { return s.id === sid; }); if (shp && shp.lot) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ feeType: fname, category: eb.category || "other", vendor: eb.vendor || "", actualCost: perLot }); } });
        });
      }
    } else if (dist.length > 0) {
      dist.forEach(function (d) { if (d.lot) { if (!expenseByLot[d.lot]) expenseByLot[d.lot] = []; var pallets = parseInt(d.pallets, 10) || 0; var cost = d.costRate ? (parseFloat(d.costRate) || 0) * pallets : (parseFloat(d.amount) || 0); expenseByLot[d.lot].push({ feeType: eb.category || "other", category: eb.category || "other", vendor: eb.vendor || "", actualCost: cost }); } });
    } else if (linkedLots.length > 0) {
      var perLot = (parseFloat(eb.amount) || 0) / linkedLots.length;
      linkedLots.forEach(function (sid) { var shp = shipments.find(function (s) { return s.id === sid; }); if (shp && shp.lot) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ feeType: eb.category || "other", category: eb.category || "other", vendor: eb.vendor || "", actualCost: perLot }); } });
    }
  });
  return expenseByLot;
}

/* Full per-lot profit computation — self-contained: given just a lot
   identifier, loads bills/invoices/shipments/expense bills fresh from
   localStorage, finds every bill/invoice tied to that lot (not just
   one — a lot can have more than one bill), and returns the complete
   picture. Callers that only need the compact shape (BillRegistry's
   "My Profit" panel) read {priceDiff, commission, feeMarkup, finance,
   total, cases, perBox}; ProfitTracker.js additionally uses revenue/
   margin/products/feeDetail/billFeesByType/actualCostsByType for its
   richer breakdowns — those come from the SAME walk, not a second one,
   so there's no way for the two to diverge again. */
export function calcLotProfit(lot) {
  var empty = { priceDiff: 0, commission: 0, feeMarkup: 0, finance: 0, total: 0, cases: 0, perBox: 0, revenue: 0, margin: 0, products: [], feeDetail: [], billFeesByType: {}, actualCostsByType: {} };
  if (lot == null || lot === "") return empty;

  var allBills = loadLS("gfi-bills", []);
  var allInvoices = loadLS("gfi-invoices", []);
  var shipments = loadLS("gfi-shipments", []);
  var expenseBills = loadLS("gfi-expense-bills", []);
  var shipByLot = {}; shipments.forEach(function (s) { if (s.lot) shipByLot[s.lot] = s; });
  var expenseByLot = buildExpenseByLot(expenseBills, shipments);

  var bills = allBills.filter(function (b) { return String(b.lot) === String(lot); });
  var invoices = allInvoices.filter(function (inv) {
    var lots = new Set();
    (inv.lines || []).forEach(function (l) { if (l.lot) lots.add(String(l.lot)); });
    if (lots.size === 0 && inv.lot != null) lots.add(String(inv.lot));
    return lots.has(String(lot));
  });
  if (bills.length === 0 && invoices.length === 0) return empty;

  /* 1. PRICE DIFFERENCE — NET sale price (after per-line client
     commission) vs reported price, summed per product. */
  var priceDiff = 0; var products = [];
  var repByProd = {}; var saleByProd = {};
  bills.forEach(function (b) { (b.lines || []).forEach(function (l) { var k = l.productName || l.productBase || "?"; var c = parseInt(l.cases, 10) || 0; if (!repByProd[k]) repByProd[k] = 0; repByProd[k] += c * (parseFloat(l.reportedPrice) || 0); }); });
  invoices.forEach(function (inv) {
    var invDefRate = parseFloat(inv.commissionRate) || 0;
    invoiceRowsForLot(inv, lot, shipByLot).forEach(function (row) {
      var k = row.product;
      var lineRate = (row.commissionRate === undefined || row.commissionRate === null || row.commissionRate === "")
        ? invDefRate
        : (parseFloat(row.commissionRate) || 0);
      var netSp = row.salePrice * (1 - lineRate / 100);
      if (!saleByProd[k]) saleByProd[k] = 0;
      saleByProd[k] += row.cases * netSp;
    });
  });
  new Set([].concat(Object.keys(repByProd), Object.keys(saleByProd))).forEach(function (k) {
    var diff = (saleByProd[k] || 0) - (repByProd[k] || 0);
    priceDiff += diff;
    products.push({ product: k, priceDiff: diff });
  });

  /* 2. COMMISSION — shipper-side commission GFI keeps from the bill,
     per-line with fallback to the bill default. */
  var commission = 0;
  bills.forEach(function (b) {
    var billDefRate = parseFloat(b.commissionRate) || 0;
    (b.lines || []).forEach(function (l) {
      if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
      var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
      var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
        ? billDefRate
        : (parseFloat(l.commissionRate) || 0);
      commission += lineAmt * (lineRate / 100);
    });
  });

  /* 3. FEE MARKUP — bill fees charged to the shipper minus GFI's
     actual cost, excluding Advance Financing Fee (handled separately
     as Finance Markup below). */
  var billFees = 0; var billFeesByType = {}; var financeCharged = 0;
  bills.forEach(function (b) { (b.fees || []).forEach(function (fe) { if (fe.passThrough) return; var amt = parseFloat(fe.amount) || 0; var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "Other"); if (fname === "Advance Financing Fee") { financeCharged += amt; } else { billFees += amt; } if (!billFeesByType[fname]) billFeesByType[fname] = 0; billFeesByType[fname] += amt; }); });
  var actualCosts = 0; var actualCostsByType = {};
  (expenseByLot[lot] || []).forEach(function (exp) { actualCosts += exp.actualCost; var cn = exp.feeType || exp.category || "other"; if (!actualCostsByType[cn]) actualCostsByType[cn] = 0; actualCostsByType[cn] += exp.actualCost; });
  var feeMarkup = billFees - actualCosts;

  /* 4. FINANCE MARKUP — invoice's Advance Financing Fee minus the
     slice of cost attributable to this lot (via transferTargetBillId,
     single-lot match, or multi-lot proration by cases). */
  var financeCost = 0;
  invoices.forEach(function (inv) {
    (inv.fees || []).forEach(function (fe) {
      var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "");
      if (fname !== "Advance Financing Fee") return;
      var amt = parseFloat(fe.amount) || 0;
      if (amt === 0) return;
      if (fe.transferTargetBillId) {
        var tb = allBills.find(function (b) { return b.id === fe.transferTargetBillId; });
        if (tb && String(tb.lot) === String(lot)) financeCost += amt;
        else if (tb && String(tb.lot) !== String(lot)) return;
        else financeCost += amt;
      } else if (inv.lot == null || String(inv.lot) === String(lot)) {
        var invLots = new Set(); (inv.lines || []).forEach(function (l) { if (l.lot) invLots.add(l.lot); });
        if (invLots.size <= 1) financeCost += amt;
        else {
          var tC = 0, lC = 0;
          (inv.lines || []).forEach(function (l) { var cs = parseInt(l.cases, 10) || 0; tC += cs; if (!l.lot || String(l.lot) === String(lot)) lC += cs; });
          financeCost += amt * (tC > 0 ? lC / tC : 0);
        }
      }
    });
  });
  var finance = financeCharged - financeCost;
  if (financeCost > 0) {
    if (!actualCostsByType["Advance Financing Fee"]) actualCostsByType["Advance Financing Fee"] = 0;
    actualCostsByType["Advance Financing Fee"] += financeCost;
  }

  var total = priceDiff + commission + feeMarkup + finance;

  var cases = 0;
  invoices.forEach(function (inv) { invoiceRowsForLot(inv, lot, shipByLot).forEach(function (row) { cases += row.cases; }); });
  if (cases === 0) { bills.forEach(function (b) { (b.lines || []).forEach(function (l) { cases += parseInt(l.cases, 10) || 0; }); }); }

  var revenue = 0;
  invoices.forEach(function (inv) { invoiceRowsForLot(inv, lot, shipByLot).forEach(function (row) { revenue += row.cases * row.salePrice; }); });
  var margin = revenue > 0 ? (total / revenue) * 100 : 0;

  var feeDetail = [];
  Object.keys(billFeesByType).forEach(function (fname) { feeDetail.push({ name: fname, charged: billFeesByType[fname], cost: 0 }); });
  (expenseByLot[lot] || []).forEach(function (exp) {
    var eName = exp.feeType || exp.category || "other";
    var match = feeDetail.find(function (fd) { return fd.name === eName; });
    if (match) match.cost += exp.actualCost;
    else feeDetail.push({ name: eName, charged: 0, cost: exp.actualCost });
  });
  if (financeCost > 0) {
    var finMatch = feeDetail.find(function (fd) { return fd.name === "Advance Financing Fee"; });
    if (finMatch) finMatch.cost += financeCost;
    else feeDetail.push({ name: "Advance Financing Fee", charged: 0, cost: financeCost });
  }

  var perBox = cases > 0 ? total / cases : 0;

  return {
    priceDiff: priceDiff, commission: commission, feeMarkup: feeMarkup, finance: finance,
    total: total, cases: cases, perBox: perBox, revenue: revenue, margin: margin,
    products: products, feeDetail: feeDetail, billFeesByType: billFeesByType, actualCostsByType: actualCostsByType,
  };
}
